import { Dashboard } from '@/components/features/Homepage/Dashboard';
import { ServiceOperationsDashboard } from '@/components/features/Homepage/ServiceOperationsDashboard';
import { ServiceWorkOrdersHomepage } from '@/components/features/Homepage/ServiceWorkOrdersHomepage';
import { AnalyticsDocsProvider } from '@/contexts/AnalyticsDocsContext';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { type BreadcrumbItem } from '@/types';
import type { DataPointDoc } from '@/types/analytics';
import { Head, usePage } from '@inertiajs/react';

interface HomepageProps {
    /** Years that actually have data, for the period filter's year select. */
    availableYears?: number[];
    /** Data-point documentation for the home dashboard cards, keyed "homepage.<card>". */
    boardDocs?: Record<string, DataPointDoc>;
    dateRange: {
        from_date: string;
        to_date: string;
    };
    dashboardData: {
        statistics: {
            totalLeads: number;
            totalProjects: number;
            totalCompanies: number;
            totalWorkOrders: number;
            monthlyRevenue: number;
            monthlyRevenueGrowth: number;
            leadsCreated: number;
            projectsCreated: number;
            workOrdersCreated: number;
        };
        monthlyRevenueSeries: Array<{
            label: string;
            leadsRevenue: number;
            projectsRevenue: number;
        }>;
        leadsProjectsTimeline: Array<{ period: string; leads: number; projects: number }>;
        leadFunnel: Array<{ status: string; count: number; ratio: number }>;
        projectStatus: {
            total: number;
            breakdown: Array<{ status: string; count: number; ratio: number }>;
        };
        recentActivity: Array<{ type: string; title: string; created_at: string | null }>;
        topSalesmen: Array<{
            id: string;
            name: string;
            avatar?: string | null;
            revenue: number;
            projectsCount: number;
        }>;
        topServiceEmployees: Array<{
            id: string;
            name: string;
            avatar?: string | null;
            completedCount: number;
        }>;
        topCompanies: Array<{
            id: string;
            name: string;
            revenue: number;
            projectsCount: number;
        }>;
    };
    workOrderStats?: {
        today: number;
        mySchedule: number;
        actionRequired: number;
        forMyReview: number;
        completed: number;
        inProgress: number;
        unscheduled: number;
    };
    serviceDashboardData?: {
        kpis: {
            total: number;
            completed: number;
            inProgress: number;
            unscheduled: number;
            overdue: number;
        };
        statusBreakdown: Array<{ status: string; count: number; ratio: number }>;
        priorityBreakdown: Array<{ priority: string; count: number; ratio: number }>;
        trend: Array<{ label: string; created: number; completed: number }>;
        topWorkers: Array<{ id: string; name: string; avatar?: string | null; completedCount: number }>;
    };
    auth: {
        user: {
            id: string;
            role?: string;
            [key: string]: unknown;
        };
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

export default function Homepage() {
    const { props } = usePage<HomepageProps>();
    const { dashboardData, workOrderStats, serviceDashboardData, auth, dateRange, availableYears } = props;
    const { t } = useTranslations();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: t('homepage.breadcrumbs.home', 'Home'),
            href: '/homepage',
        },
    ];

    // Check if user is ServiceWorker or ServiceSupervisor
    const userRole = auth?.user?.role;
    const isServiceWorker = userRole === 'Service Worker';
    const isServiceSupervisor = userRole === 'Service Supervisor';
    const isServiceManager = userRole === 'Service Manager';
    const showServiceOperationsDashboard = (isServiceSupervisor || isServiceManager) && serviceDashboardData;
    const showWorkOrdersHomepage = isServiceWorker && workOrderStats;

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('homepage.title', 'Home')} />
            {/* Every card looks its "(i)" documentation up from here, exactly as the analytics boards do. */}
            <AnalyticsDocsProvider docs={props.boardDocs}>
                <div className="flex flex-col gap-4 p-4">
                    {showServiceOperationsDashboard ? (
                        <ServiceOperationsDashboard serviceDashboardData={serviceDashboardData!} dateRange={dateRange} />
                    ) : showWorkOrdersHomepage ? (
                        <ServiceWorkOrdersHomepage stats={workOrderStats} isSupervisor={isServiceSupervisor} />
                    ) : (
                        <Dashboard dashboardData={dashboardData} dateRange={dateRange} availableYears={availableYears} />
                    )}
                </div>
            </AnalyticsDocsProvider>
        </AppLayout>
    );
}
